// WS-MODULE-STATE-SCOPE-1 — the STATE half of per-module scoping.
//
// ─────────────────────────────────────────────────────────────────────────────
// THE CONTRACT
// ─────────────────────────────────────────────────────────────────────────────
//
// 1. Every installed/takeover module gets ONE module-local reactive store — its
//    `moduleStateRoot`. Inside that module's own surfaces (its pages, its home
//    widget bodies, its chrome rail), `s.root` resolves to THAT store, not to
//    the shell root.
//
// 2. READS FALL BACK. A key the module never wrote resolves on the shell root
//    and subscribes the reading effect to the SHELL's signal — so `activeWorkspace`,
//    `user`, `themeMode`, the capability bridges and the approval trio keep
//    working inside a module exactly as before, and a shell write still
//    re-renders module UI.
//
// 3. WRITES LAND MODULE-LOCALLY by default. Two installed apps that both write
//    `natLoading` / `natSalons` / `natCustomersLoaded` no longer clobber each
//    other, and a shell subscriber never fires on a module-local write.
//
// 4. SHELL-OWNED KEYS WRITE THROUGH. For every key in the shell's own declared
//    schema (`SHELL_OWNED_STATE_KEYS` below — STATE_DEFAULTS plus the reviewed
//    WORKSPACE_SCOPED / WORKSPACE_UNSCOPED / WORKSPACE_SCOPE_UNREVIEWED
//    partition of state.js + state-app.js) the module store's SIGNAL *is* the
//    shell's signal. A module that closes the shared dropdown with
//    `s.root.update({ contextDropdown: null })` still moves the shell's value
//    and still notifies every shell subscriber; it cannot silently shadow a
//    shell key with a module-local copy.
//
// 5. EXPLICIT ROOT OPT-IN, two forms:
//      • `s.root.shell` is the shell root store itself — `s.root.shell.update({…})`
//        writes the shell plane ON PURPOSE. (Standalone, outside the shell, the
//        module store does not exist and `s.root.shell` is undefined: write
//        `(s.root.shell || s.root).update(…)` in code that must run both ways.)
//      • a module manifest may declare `rootStateKeys: ['x', 'y*']`, which adds
//        those keys to the write-through set for that module only. Same entry
//        grammar as `scopedKeys` (plain key or `prefix*`), read by the same
//        normalizer in moduleScopedKeys.js.
//
// ─────────────────────────────────────────────────────────────────────────────
// WHY THIS NEEDS NO FRAMEWORK CHANGE — the engine already has the concept
// ─────────────────────────────────────────────────────────────────────────────
//
// `@symbo.ls/signal` src/store.js ALREADY implements "child store with a parent
// fallback", and it implements exactly the semantics this contract wants:
//
//   • its `get` trap ends with «Delegate to root state for keys not found
//     locally»: `if (prop !== 'root' && target.root && target.root !== proxy)
//     return target.root[prop]` — a read miss resolves on `store.root` and the
//     observer subscribes to the OWNER's signal. It even mirrors a local
//     placeholder signal when the key is missing everywhere, so a value that
//     arrives on the parent later is still seen.
//   • its `set` trap has no upward walk: a new key lands on THIS store's own
//     signal. Writes are local by construction.
//
// So a module store is `createStore({})` with `store.root = shellRoot`. Nothing
// is forked, nothing is proxied twice, and no smbls change is required for the
// default half of the contract.
//
// The one thing the engine does NOT have is a KEYED WRITE-THROUGH (`createScopeStore`
// has the opposite, unconditional, write-to-the-owner rule — right for the scope
// chain, wrong for state). Rather than wrap `s.root` in a shell-side Proxy — which
// would fork the read path, break `isStore`, and hide the engine's own tracking —
// point 4 is implemented by ALIASING THE SIGNAL OBJECT: for a shell-owned key the
// module store's entry in its signals map IS the shell store's signal, obtained
// through the engine's own public `getSignals()` accessor. Both traps then behave
// correctly with no interception at all: `hasOwnProperty(signals, key)` is true, so
// a read returns the shell's value (and subscribes to the shell's signal) and a
// write calls `.set()` on the shell's signal (so the shell's subscribers notify).
//
// IF A FRAMEWORK PRIMITIVE IS EVER WANTED, this is its precise shape:
//   createStore(initial, { parent, writeThrough: (key) => boolean })
// i.e. the existing `root` delegation plus a per-key predicate consulted by the
// `set` trap before it mints a local signal. That would replace `aliasShellOwnedKeys`
// below with one option, and it would also remove this file's one blemish — a
// shell-owned key that has no signal yet has to be MINTED on the shell store so
// there is something to alias (see `aliasShellOwnedKeys`). That is an fw-squad
// change; it is NOT required for this contract to be correct, so the workspace
// half ships against the primitive that exists.
//
// ─────────────────────────────────────────────────────────────────────────────
// This file deliberately imports NOTHING but the shell's own state registry —
// the reactive engine arrives as an injected `engine` bag (the same
// `import('smbls')` the loader already awaits for the module design system), so
// there is exactly ONE reactive engine on the page and this module stays
// testable without a DOM.

import {
  STATE_DEFAULTS,
  WORKSPACE_SCOPED_KEYS,
  WORKSPACE_UNSCOPED_KEYS,
  WORKSPACE_SCOPE_UNREVIEWED
} from '../state.js'

// THE SHELL-OWNED KEY SET — single-sourced from the shell's own schema, so a
// key added to state.js / state-app.js is shell-owned the moment it is declared
// and no second list can drift. `STATE_DEFAULTS` is the schema; the three
// partition sets add the ~50 keys the shell writes at runtime without declaring
// a default (`_homeHiddenWidgets`, `_dropdown`-adjacent UI latches, the logs /
// analytics slices, …). `scopes.partition.test.js` already fails if any shell
// key belongs to none of the three, which is what keeps this union complete.
export const SHELL_OWNED_STATE_KEYS = new Set([
  ...Object.keys(STATE_DEFAULTS),
  ...WORKSPACE_SCOPED_KEYS,
  ...WORKSPACE_UNSCOPED_KEYS,
  ...WORKSPACE_SCOPE_UNREVIEWED
])

// A module's own extra write-through declarations (`manifest.rootStateKeys`),
// normalized to the same `{ key } | { prefix }` entries `scopedKeys` uses.
const _coversKey = (entries, key) => {
  for (const e of entries || []) {
    if (e.key !== undefined && e.key === key) return true
    if (e.prefix !== undefined && key.startsWith(e.prefix)) return true
  }
  return false
}

// Is `key` written through to the shell root from inside a module?
export const isShellOwnedStateKey = (key, rootStateKeys) =>
  SHELL_OWNED_STATE_KEYS.has(key) || _coversKey(rootStateKeys, key)

// The keys a given module writes through: the shell schema plus any EXPLICIT
// key the module declared. A `prefix*` declaration cannot be pre-expanded (it
// has no key list), so it is honoured on demand by `isShellOwnedStateKey` and
// re-checked by every alias sync against the shell store's live key list.
const _writeThroughKeys = (shellSignals, rootStateKeys) => {
  const out = new Set(SHELL_OWNED_STATE_KEYS)
  for (const e of rootStateKeys || []) {
    if (e.key !== undefined) out.add(e.key)
    else if (e.prefix !== undefined && shellSignals) {
      for (const k of Object.keys(shellSignals)) if (k.startsWith(e.prefix)) out.add(k)
    }
  }
  return out
}

// Alias every write-through key's SIGNAL from the shell store into the module
// store, so reads and writes on that key resolve to ONE signal shared by both.
//
// A key with no signal on the shell store yet is MINTED there as
// `createSignal(undefined)` — there has to be an object to share, and a module
// write to a shell key that the shell has not written yet must still reach the
// shell rather than silently shadow it. This is not a new hazard class: the
// store's own `get` trap already mints exactly such placeholder signals on any
// tracked read of an absent key (see its "Lazy signal creation" branch), and a
// signal holding `undefined` reads identically to an absent key. Bounded and
// named: only keys the shell itself declares.
//
// Idempotent, and re-run on every loader pass — a shell `clean()`/`replace()`
// that deleted a signal would otherwise leave the module holding an orphan.
export const aliasShellOwnedKeys = (moduleStore, shellRoot, engine, rootStateKeys) => {
  const getSignals = engine && engine.getSignals
  const createSignal = engine && engine.createSignal
  if (!moduleStore || !shellRoot || typeof getSignals !== 'function') return 0
  const shellSignals = getSignals(shellRoot)
  const modSignals = getSignals(moduleStore)
  if (!shellSignals || !modSignals) return 0
  let n = 0
  for (const key of _writeThroughKeys(shellSignals, rootStateKeys)) {
    let sig = Object.prototype.hasOwnProperty.call(shellSignals, key) ? shellSignals[key] : null
    if (!sig) {
      if (typeof createSignal !== 'function') continue
      sig = createSignal(undefined)
      shellSignals[key] = sig
    }
    if (modSignals[key] !== sig) {
      modSignals[key] = sig
      n++
    }
  }
  return n
}

// ONE ENGINE, PROVEN — not assumed.
//
// @symbo.ls/signal's engineRegistry makes copies of the SAME version adopt a
// single function table, but copies of DIFFERENT versions deliberately do not
// (see its own comment: impersonating a fossil engine is the worse failure).
// On such a page the shell root belongs to engine B while `import('smbls')`
// hands back engine A, and every A/B mix here fails SILENTLY rather than
// loudly: A's `getSignals` reads a Symbol B's store never answers to (so the
// write-through alias would no-op), and a read of a B signal from inside an A
// effect registers in B's observer, which A never flushes (so a module would
// stop re-rendering on shell changes).
//
// So the engine is not trusted, it is PROVEN: the only usable engine is one
// whose `getSignals` actually resolves the shell root's own signal map. If none
// does, scoping is refused and every module stays on the shell root — exactly
// the pre-WS-MODULE-STATE-SCOPE-1 behaviour, with the fault named once.
let _warnedEngineSplit = false
export const engineOwnsStore = (engine, store) => {
  const getSignals = engine && engine.getSignals
  if (typeof getSignals !== 'function' || !store) return false
  try {
    const signals = getSignals(store)
    return !!signals && typeof signals === 'object'
  } catch (_) {
    return false
  }
}

export const __resetModuleStateScopeWarnings = () => {
  _warnedEngineSplit = false
  _warnedNoEngineSlot = false
}

// Build one module's state root. `engine` is the smbls barrel (or any bag
// exposing createStore / getSignals / createSignal / applyStateMethods).
// Returns null when the engine is unusable — the caller then leaves the module
// on the shell root, which is exactly the pre-WS-MODULE-STATE-SCOPE-1 behaviour
// (soft-fail: a module never goes blank because scoping could not be built).
export const createModuleStateRoot = (shellRoot, engine, { rootStateKeys } = {}) => {
  const createStore = engine && engine.createStore
  if (!shellRoot || typeof createStore !== 'function') return null
  // A non-store root (a plain bag: tests, a shell that has not booted its
  // reactive root yet) is not something to scope — quietly decline.
  const isStore = engine.isStore
  if (typeof isStore === 'function' && !isStore(shellRoot)) return null
  if (!engineOwnsStore(engine, shellRoot)) {
    if (!_warnedEngineSplit) {
      _warnedEngineSplit = true
      // MEASURED, and the reason this guard is not paranoia: in a checkout
      // where `smbls` resolves to a STALE `smbls/packages/smbls/dist/smbls.cjs`
      // (it inlines its own @symbo.ls/signal copy at build time) beside a newer
      // `@symbo.ls/signal` source, the two versions do not adopt each other and
      // this branch is taken. The fix is upstream — rebuild/republish smbls so
      // the dist and the resolved signal agree — never a workaround here.
      console.warn(
        '[workspaceModule] per-module state scoping is OFF: the reactive engine that ' +
          'owns the shell root is not the one this bundle imported (duplicate ' +
          '@symbo.ls/signal versions — the framework prints its own ' +
          '"duplicate @symbo.ls/signal instance" diagnostic beside this). Modules ' +
          'keep writing the shared root store; align the signal version so exactly ' +
          'one engine resolves.'
      )
    }
    return null
  }
  const store = createStore({})
  // The engine's OWN parent link. Assigning `root` goes through the store's
  // `set` trap, which stores it as a plain (non-signal, non-enumerable-to-parse)
  // property — the same field `element/src/create.js` writes when it chains a
  // child state to its parent.
  store.root = shellRoot
  // `s.root.shell` — the explicit write-to-the-shell channel. Defined on the
  // RAW target, non-enumerable: the `get` trap returns `target[prop]` before it
  // ever delegates, and `ownKeys` / `$parse` only ever walk the signals map, so
  // this is invisible to every clone, fingerprint and wipe.
  try {
    Object.defineProperty(store.__raw, 'shell', {
      value: shellRoot,
      enumerable: false,
      configurable: true,
      writable: true
    })
    // The engine this store was built with, so the scope plugin can mint
    // sibling stores from the SAME engine without importing one.
    Object.defineProperty(store.__raw, ENGINE_SLOT, {
      value: engine,
      enumerable: false,
      configurable: true,
      writable: true
    })
  } catch (_) {
    /* frozen raw — `s.root.shell` is simply unavailable, contract point 5a */
  }
  // v3 state API (set/toggle/rootUpdate/setByPath/…). The store proxy already
  // answers update/parse/replace/add/…; these are the state package's extras,
  // and module code calls them. Applied AFTER `store.root` is assigned: the
  // helper only installs its own parent-walking `root` getter when `raw` has no
  // own `root` descriptor, and that getter would ignore this link.
  const applyStateMethods = engine && engine.applyStateMethods
  if (typeof applyStateMethods === 'function') {
    try {
      applyStateMethods(null, store)
    } catch (_) {
      /* extras unavailable — update/parse/replace still work via the proxy */
    }
  }
  aliasShellOwnedKeys(store, shellRoot, engine, rootStateKeys)
  return store
}

// ── Carrying the scope into the tree — a CONTEXT PLUGIN, not a def property ──
//
// The first shape of this was `state: {} + onInit` written onto every module
// def. It is WRONG, and the reason is worth keeping: `applyExtends`
// (smbls element/src/extends.js) merges the extends chain onto the element
// FIRST and the definition's own keys SECOND, so a definition key always wins —
// which means an `onInit` added by the shell would have SILENTLY REPLACED the
// `onInit` a module component declares behind `extends`, and an added `state`
// would have replaced its declared state. The two shell call sites that mount
// a module surface by name (`{ extends: <rail> }`, `{ extends: <widgetBody> }`)
// are exactly that shape, so the failure would have been invisible and routine.
//
// `context.plugins` is the carrier that cannot be shadowed: `triggerLifecycle`
// runs every plugin's hook for EVERY element whose context has plugins, before
// the element's own handler, and DOMQL hands an element's context to its whole
// subtree. The module context already reaches every module surface (that is how
// the module design system and the module function/component scope travel), so
// the state scope rides the same carrier and needs no def mutation at all.
//
// The hook runs once per element and short-circuits in one comparison for
// everything already inside the scope.
export const createModuleStatePlugin = (moduleStore) => ({
  init(el) {
    if (!el || !moduleStore) return
    const s = el.state
    if (!s || s === moduleStore) return
    // Already inside this module's scope (the common case — every descendant).
    if (s.root === moduleStore) return
    const shellRoot = moduleStore.root
    // (1) The element inherited the APP ROOT itself (`s.root === s`: the shell
    //     root, or any self-referencing root). There is nothing local to
    //     preserve, and re-pointing the app root's own `root` would re-home the
    //     entire shell — so this element gets its OWN store instead, chained to
    //     the module root. The shell's keys stay readable through the module
    //     store's fallback.
    if (s === shellRoot || s.root === s) {
      _adoptOwnStore(el, moduleStore)
      return
    }
    // (2) A state that belongs to a shell HOST of this module surface (the home
    //     board's per-widget child state, a slot's child state, …). Re-pointing
    //     its `root` keeps every local key of that state intact — `s.spec`,
    //     `s.moduleId`, `s.body` are still there — and only changes what
    //     `s.root` MEANS inside this subtree. Shell code reading that host
    //     state is unaffected in practice: everything it reads or writes on
    //     `s.root` is a shell-owned key, which the module store shares with the
    //     shell store signal-for-signal (see aliasShellOwnedKeys).
    try {
      s.root = moduleStore
    } catch (_) {
      /* frozen state — the subtree keeps the shell root, same as before */
    }
  }
})

// Give `el` its own reactive store chained to the module root — the same wiring
// `element/src/create.js` does for a declared `state:` object (own store,
// `__element`, `parent`, the parent's `__children` entry, the v3 method set),
// with `root` pointed at the module store instead of at the app root.
let _warnedNoEngineSlot = false
const _adoptOwnStore = (el, moduleStore) => {
  const engine = _engineOf(moduleStore)
  if (!engine) {
    // Never a silent no-op: without an engine this element keeps the shell
    // root and the module is unscoped from here down — say so once.
    if (!_warnedNoEngineSlot) {
      _warnedNoEngineSlot = true
      console.warn(
        '[workspaceModule] a module state root was built without its engine ' +
          'stamp — the subtree stays on the shell root. Build module stores ' +
          'with createModuleStateRoot().'
      )
    }
    return
  }
  try {
    const parentState = el.parent && el.parent.state
    const store = engine.createStore({})
    store.__element = el
    if (parentState) {
      store.parent = parentState
      if (parentState.__children && el.key != null) parentState.__children[el.key] = store
    }
    store.root = moduleStore
    if (typeof engine.applyStateMethods === 'function') engine.applyStateMethods(el, store)
    el.state = store
  } catch (e) {
    console.warn('[workspaceModule] could not scope a module subtree’s state:', e && e.message)
  }
}

// The engine a module store was built with — stashed on the store's raw target
// at creation so the plugin can mint sibling stores from the SAME engine
// without importing one (see the one-engine note above).
const ENGINE_SLOT = '__moduleStateEngine'
const _engineOf = (store) => {
  try {
    return (store && store.__raw && store.__raw[ENGINE_SLOT]) || null
  } catch (_) {
    return null
  }
}

// Attach the module's state scope to its resolution context. `shellCtx` is the
// SHELL context `modCtx` prototype-chains to: `plugins` must be an OWN array on
// `modCtx` (a prototype read would return the shell's array and pushing into it
// would install this module's plugin app-wide), so the shell's plugins are
// copied in front of it.
export const attachModuleStateScope = (modCtx, shellCtx, moduleStore) => {
  if (!modCtx || !moduleStore) return false
  const inherited = (shellCtx && Array.isArray(shellCtx.plugins) && shellCtx.plugins) || []
  const own = Object.prototype.hasOwnProperty.call(modCtx, 'plugins') ? modCtx.plugins : null
  if (Array.isArray(own) && own.some((p) => p && p.__moduleStateScope === moduleStore)) return true
  const plugin = createModuleStatePlugin(moduleStore)
  plugin.__moduleStateScope = moduleStore
  modCtx.plugins = [...inherited, plugin]
  return true
}

// The module state root a resolution context scopes its subtree to — the store
// `attachModuleStateScope` put on that context — or null: a shell context, a
// module whose scoping was declined, or one not set up yet (its surfaces then
// read and write the shell root, and so must every call made for them).
export const moduleStateScopeOf = (ctx) => {
  if (!ctx || typeof ctx !== 'object') return null
  const own = Object.prototype.hasOwnProperty.call(ctx, 'plugins') ? ctx.plugins : null
  if (!Array.isArray(own)) return null
  for (const p of own) if (p && p.__moduleStateScope) return p.__moduleStateScope
  return null
}

// The shell root behind a state root: a module's store links to the shell as
// its own `root`; the shell root's `root` is itself (and a plain bag has none).
// A meta read — `root` is never a signal, so nothing subscribes.
export const shellRootOf = (root) =>
  root && typeof root === 'object' && root.root && root.root !== root ? root.root : root

// ── A SHELL HOST that mounts module surfaces on request ──────────────────────
//
// The plugin scopes a module element at its Init. But smbls evaluates `if:`
// BEFORE Init (element create: state, then the `if` check, then the Init
// lifecycle), and an element whose `if:` is false at creation stays parked —
// no Init at all — until it first shows. So a module form gated on its own root
// (`if: (el, s) => !!s.root.<draft>`, the aihouse AhListingCreate) reads its
// PARENT's state on that first evaluation, and a call made on it while it is
// parked (the app's `open`, seeding the draft) runs there too. Mounted into a
// shell host, that parent state is the SHELL root: the seed landed on the
// shell, the form's own close landed in the module store, and from then on the
// module's own `null` shadowed every later seed — the sheet opened once per
// page load (WS-ASSISTANT-APP-FORM-OPEN-SHELL-SCOPE-1).
//
// So such a host keeps a state of its OWN (`state: {}` — never the shell root
// itself, which cannot be re-pointed without re-homing the whole shell) and
// points it at the scope of the surface it is about to mount, BEFORE the mount:
// the module's state root, or the shell root for a shell surface (and back
// again — a host serves every app). That is the plugin's case (2) above, done
// before the surface's first read instead of at its Init. The host's own reads
// on `s.root` are shell-owned keys, which the module store shares with the
// shell signal-for-signal (aliasShellOwnedKeys).
//
// `root` reads are meta reads on a store (never tracked), so this subscribes
// nothing. Returns the shell root, or null when the host cannot be scoped (no
// state of its own): the caller then mounts as before.
export const scopeHostState = (host, moduleStore) => {
  const s = host && host.state
  if (!s || typeof s !== 'object') return null
  const current = s.root
  if (!current || typeof current !== 'object') return null
  const shellRoot = shellRootOf(current)
  if (s === shellRoot || s === current) return null
  // A store chained to another shell root is stale (the switch pipeline can
  // replace the root store): the shell root is the safe scope then.
  const target = moduleStore && moduleStore.root === shellRoot ? moduleStore : shellRoot
  if (current !== target) s.root = target
  return shellRoot
}
